/**
 * Pure verdict/paywall key pickers + date formatting — extracted from App.js
 * (Jun 2026 refactor). No React, no state — safe to import anywhere.
 */

export const getVerdictKey = (riskLevel) => {
  switch (riskLevel) {
    case 'LOW': return 'verdictWorthBuying';
    case 'MEDIUM': return 'verdictBeCareful';
    case 'HIGH': return 'verdictAvoid';
    default: return 'verdictBeCareful';
  }
};

// Short paywall teaser key — neutral phrasing that works for both used cars
// (with MOT history) and new cars (no history yet). We deliberately avoid
// mentioning "history" / "known issues" here so we don't lie to users
// looking at a brand-new vehicle.
export const getPaywallTeaserKey = (riskLevel, analysisData) => {
  if (analysisData?.price_check?.is_scam_risk) return 'paywallTeaserScam';
  switch (riskLevel) {
    case 'LOW': return 'paywallTeaserLow';
    case 'MEDIUM': return 'paywallTeaserMedium';
    case 'HIGH': return 'paywallTeaserHigh';
    default: return 'paywallTeaserMedium';
  }
};

export const getVerdictBg = (riskLevel) => {
  switch (riskLevel) {
    case 'LOW': return '/bg-good.jpg';
    case 'MEDIUM': return '/bg-caution.jpg';
    case 'HIGH': return '/bg-bad.jpg';
    default: return '/bg-caution.jpg';
  }
};

// Post-payment verdict: short one-liner (the long enticement copy lives on the paywall).
export const getVerdictShortExplanationKey = (riskLevel, analysisData) => {
  if (analysisData?.price_check?.is_scam_risk) return 'verdictShortExplanationScam';
  switch (riskLevel) {
    case 'LOW': return 'verdictShortExplanationLow';
    case 'MEDIUM': return 'verdictShortExplanationMedium';
    case 'HIGH': return 'verdictShortExplanationHigh';
    default: return 'verdictShortExplanationMedium';
  }
};

// Map the app's UI language → BCP-47 locale for date formatting. Dates must
// follow the SELECTED UI LANGUAGE, never navigator.language (a pl-PL browser
// with the UI set to English must still see English month names).
const UI_DATE_LOCALES = { en: 'en-GB', pl: 'pl-PL', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' };
export const uiDateLocale = (language) => UI_DATE_LOCALES[language] || 'en-GB';

// Timezone-safe date formatter: parses DVSA-style "YYYY-MM-DD" as a local
// date so a user west of UTC never sees "01 Sep 2026" silently rendered as
// "31 Aug 2026". Falls back to native parsing for full ISO timestamps that
// carry their own offset (e.g. "2026-09-01T10:30:00Z").
export const formatDate = (dateStr, locale = 'en-GB') => {
  if (!dateStr) return "";
  const s = String(dateStr);
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (dateOnly) {
    const y = parseInt(dateOnly[1], 10);
    const mo = parseInt(dateOnly[2], 10) - 1;
    const da = parseInt(dateOnly[3], 10);
    return new Date(y, mo, da, 12, 0, 0).toLocaleDateString(locale);
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString(locale);
};
